import { FileWarning, ScrollText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import type { BookExportJob, BookExportReportEntry } from "@/features/book-assembly/book-assembly.types";
import { hasBookExportReportEntries } from "@/lib/book-assembly/export-report";

const sectionLabelKeys: Record<string, string> = {
  characters_appendix: "bookAssembly.summary.sectionLabels.characters_appendix",
  cover: "bookAssembly.summary.sectionLabels.cover",
  end_matter: "bookAssembly.summary.sectionLabels.end_matter",
  lore_appendix: "bookAssembly.summary.sectionLabels.lore_appendix",
  main_story: "bookAssembly.summary.sectionLabels.main_story",
  side_stories: "bookAssembly.summary.sectionLabels.side_stories",
  table_of_contents: "bookAssembly.summary.sectionLabels.table_of_contents",
  timeline_appendix: "bookAssembly.summary.sectionLabels.timeline_appendix",
  title_page: "bookAssembly.summary.sectionLabels.title_page",
};

function getEntryKindLabelKey(kind: BookExportReportEntry["kind"]) {
  switch (kind) {
    case "omission":
      return "bookAssembly.report.entryKinds.omission";
    case "warning":
      return "bookAssembly.report.entryKinds.warning";
    default:
      return "bookAssembly.report.entryKinds.fallback";
  }
}

function getEntryKindVariant(kind: BookExportReportEntry["kind"]) {
  switch (kind) {
    case "warning":
      return "default" as const;
    case "omission":
      return "secondary" as const;
    default:
      return "outline" as const;
  }
}

export function BookExportReportPanel({
  job,
}: {
  job: BookExportJob | null;
}) {
  const { t } = useTranslation();

  return (
    <section
      className="space-y-5 border-t border-ink/10 pt-6"
      data-testid="book-export-report-panel"
    >
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-clay">
              {t("bookAssembly.report.eyebrow")}
            </p>
            <h3 className="text-2xl text-foreground">{t("bookAssembly.report.title")}</h3>
            <p className="text-sm leading-7 text-muted-foreground">
              {t("bookAssembly.report.body")}
            </p>
          </div>
          <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground sm:flex">
            {hasBookExportReportEntries(job?.report) ? (
              <FileWarning className="h-6 w-6" />
            ) : (
              <ScrollText className="h-6 w-6" />
            )}
          </div>
        </div>

        {job?.report ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="border-t border-ink/10 py-4 text-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {t("bookAssembly.report.generatedAt")}
              </p>
              <p className="mt-2 font-medium text-foreground" data-testid="book-export-report-generated-at">
                {job.report.generatedAt}
              </p>
            </div>
            <div className="border-t border-ink/10 py-4 text-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {t("bookAssembly.report.generatedSections")}
              </p>
              <p className="mt-2 font-medium text-foreground" data-testid="book-export-report-generated-sections">
                {job.report.generatedSections.length > 0
                  ? job.report.generatedSections
                    .map((sectionKey) => t(sectionLabelKeys[sectionKey] ?? sectionKey))
                    .join(", ")
                  : t("bookAssembly.report.noSections")}
              </p>
            </div>
          </div>
        ) : (
          <div className="border-y border-ink/10 py-4 text-sm text-muted-foreground">
            {t("bookAssembly.report.unavailable")}
          </div>
        )}

        {hasBookExportReportEntries(job?.report) ? (
          <div className="space-y-3">
            {job?.report?.entries.map((entry, index) => (
              <div
                className="border-t border-ink/10 py-4 text-sm first:border-t-0"
                key={`${entry.kind}-${entry.code}-${entry.sourceId ?? index}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="font-medium text-foreground">{entry.message}</p>
                    <p className="text-muted-foreground">
                      {t(sectionLabelKeys[entry.sectionKey] ?? entry.sectionKey)}
                    </p>
                  </div>
                  <Badge variant={getEntryKindVariant(entry.kind)}>
                    {t(getEntryKindLabelKey(entry.kind))}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-y border-ink/10 py-4 text-sm text-muted-foreground">
            {t("bookAssembly.report.empty")}
          </div>
        )}
    </section>
  );
}
